/**
 * src/api/StubGateway.ts — implementação em MEMÓRIA do `ReceptorGateway`.
 *
 * Serve a três coisas, todas igualmente importantes:
 *  1. os testes do motor (relógio e latência controlados, `304` e falha induzidos);
 *  2. o modo demonstração (`?demo=1`) — abrir a URL e VER o loop, sem backend;
 *  3. o dev-pleno rodando a casca sem servidor no ar.
 *
 * A mídia é gerada LOCALMENTE (SVG para imagem, MP4 embutido para vídeo) — nenhum
 * arquivo binário no repositório, nenhuma dependência de CDN. Um blob inválido
 * faria o `<img>` quebrar e a TV ficar preta, que é exatamente o defeito que este
 * gateway existe para NÃO esconder.
 */
import type { Ad, ID, MediaAsset } from '../domain/types';
import { buildPlaybackQueue } from '../domain/rotation';
import type { HeartbeatResult, QueuePayload, ReceptorGateway } from './types';

export interface StubGatewayOptions {
  /** Relógio injetável: `now()` do gateway E do motor vêm daqui. */
  now?: () => Date;
  /** Latência simulada da rede (ms). 0 nos testes de unidade, ~150 na demo. */
  latencyMs?: number;
  /** Nome da tela — a UI de espera mostra ("TV Balcão"). */
  screenName?: string;
  /** `true` = o gateway falha o download de `medVideo` (critério 5 da §9). */
  failVideoDownload?: boolean;
  /** Fila inicial. Por padrão, o conjunto de 3 anúncios abaixo. */
  ads?: Ad[];
  /** Dispara um `queue.updated` no primeiro subscribe (SSE funciona na demo). */
  realtimeOnSubscribe?: boolean;
}

/** Relógio padrão: o de verdade. */
const defaultNow = (): Date => new Date();

/** Miniatura/arte de um anúncio de imagem, em SVG (vector = nítido a 3 m, 4 KB). */
function svgPoster(title: string, subtitle: string, bg: string, fg: string): Blob {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080">` +
    `<rect width="1920" height="1080" fill="${bg}"/>` +
    `<rect x="60" y="60" width="1800" height="960" fill="none" stroke="${fg}" stroke-width="6" opacity="0.35"/>` +
    `<text x="960" y="470" font-family="Helvetica,Arial,sans-serif" font-size="150" font-weight="700" fill="${fg}" text-anchor="middle">${escapeXml(title)}</text>` +
    `<text x="960" y="620" font-family="Helvetica,Arial,sans-serif" font-size="70" fill="${fg}" opacity="0.85" text-anchor="middle">${escapeXml(subtitle)}</text>` +
    `<text x="960" y="960" font-family="Helvetica,Arial,sans-serif" font-size="44" fill="${fg}" opacity="0.5" text-anchor="middle">SIMPLEINDOR · DEMONSTRAÇÃO</text>` +
    `</svg>`;
  return new Blob([svg], { type: 'image/svg+xml' });
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * MP4 real de demonstração (H.264 baseline, 3 s, 2.4 KB), gerado com ffmpeg:
 *   ffmpeg -f lavfi -i "color=c=0x1c2233:s=160x90:r=10:d=3" \
 *          -vf "drawtext=text='SIMPLEINDOR':fontcolor=white:fontsize=22:x=(w-text_w)/2:y=(h-text_h)/2:enable='lt(mod(t\,1)\,2)'" \
 *          -c:v libx264 -profile:v baseline -pix_fmt yuv420p -preset veryslow -crf 40 \
 *          -g 30 -keyint_min 30 -sc_threshold 0 -tune stillimage -an -movflags +faststart
 * sha256 do arquivo: 05e044cfe13729633059e2834d73b565f17cfb8a7704bb429ea2e3aaa878eb9b
 *
 * POR QUE UM BINÁRIO DE VERDADE E NÃO UM MP4 FABRICADO: o §9 exige "vídeo toca mudo,
 * tela cheia, uma vez por volta" e o `?demo=1` é onde isso vai ser inspecionado. Sem
 * um MP4 real, o `<video>` nunca emite `canplay`/`ended`, e a demo não prova nada do
 * caminho de vídeo — o motor inteiro pareceria funcionar sem nunca ser exercitado.
 *
 * POR QUE VIRAR `data:` URL E NÃO FICAR COMO ASSET: o Vite embute arquivos abaixo de
 * `assetsInlineLimit` (4 KB) no próprio bundle. 2.4 KB cabe, e isso é o resultado
 * MELHOR para uma TV: zero requisição extra, funciona em `file://`, funciona sem
 * rede. Um asset separado custaria um round-trip e falharia no `file://`.
 */
import demoVideoFile from './demo-media/demo-video.mp4?url';

/**
 * Os bytes do MP4 embutido. Se o asset não for servido (binário ausente no `dist/`),
 * devolvemos um Blob vazio com o MIME certo e o `ui/player.ts` trata como mídia
 * indisponível — item pulado, nunca tela preta.
 */
async function demoVideoBlob(): Promise<Blob> {
  try {
    const response = await fetch(demoVideoFile);
    if (!response.ok) {
      return new Blob([], { type: 'video/mp4' });
    }
    return await response.blob();
  } catch {
    return new Blob([], { type: 'video/mp4' });
  }
}

/** Os 3 anúncios da demonstração: 1 vídeo + 2 imagens (brief 01 §2). */
export function demoAds(screenId: ID, now: Date): Ad[] {
  const iso = now.toISOString();
  return [
    {
      id: 'ad_demo_video',
      accountId: 'acc_demo',
      title: 'Vídeo de demonstração (MP4)',
      media: {
        id: 'med_demo_video',
        kind: 'video',
        url: 'stub://media/med_demo_video',
        thumbnailUrl: 'stub://thumb/med_demo_video',
        durationSeconds: 3,
        width: 64,
        height: 64,
      },
      screenIds: [screenId],
      schedule: { mode: 'now' },
      status: 'active',
      createdAt: iso,
      updatedAt: iso,
    },
    {
      id: 'ad_demo_imagem_1',
      accountId: 'acc_demo',
      title: 'Café da manhã',
      media: {
        id: 'med_demo_img_1',
        kind: 'image',
        url: 'stub://media/med_demo_img_1',
        thumbnailUrl: 'stub://thumb/med_demo_img_1',
        width: 1920,
        height: 1080,
      },
      screenIds: [screenId],
      schedule: { mode: 'now' },
      status: 'active',
      createdAt: iso,
      updatedAt: iso,
    },
    {
      id: 'ad_demo_imagem_2',
      accountId: 'acc_demo',
      title: 'Promoção da semana',
      media: {
        id: 'med_demo_img_2',
        kind: 'image',
        url: 'stub://media/med_demo_img_2',
        thumbnailUrl: 'stub://thumb/med_demo_img_2',
        width: 1920,
        height: 1080,
      },
      screenIds: [screenId],
      schedule: { mode: 'now' },
      status: 'active',
      createdAt: iso,
      updatedAt: iso,
    },
  ];
}

/**
 * O erro que o stub lança é o `GatewayError` REAL do módulo do dev-pleno
 * (`net/errors.ts`), não uma cópia: dois `GatewayError` com nomes diferentes
 * fariam o `catch` do receptor errar a rota da falha. O contrato é estrutural
 * (duck typing em `code`), então se `net/` for movido o conserto é um caminho.
 * Pendência de fronteira registrada no SEAM.md.
 */
import { GatewayError } from '../../net/errors';

const MEDIA_LABELS: Record<string, { title: string; subtitle: string; bg: string; fg: string }> = {
  med_demo_img_1: {
    title: 'CAFÉ DA MANHÃ',
    subtitle: 'Pão na chapa + café, até as 11h',
    bg: '#3b2417',
    fg: '#f7e7d3',
  },
  med_demo_img_2: {
    title: 'SEMANA DO CLIENTE',
    subtitle: '10% OFF em toda a loja',
    bg: '#12303c',
    fg: '#d8f1ff',
  },
};

/**
 * O gateway em memória.
 *
 * Reproduz de propósito o comportamento que o receptor tem de aguentar:
 *  - `fetchQueue(knownVersion)` devolve `null` quando a `version` não mudou (o `304`
 *    do backend) — e isso é SUCESSO, não erro;
 *  - latência simulada, para o motor não rodar em tempo irreal nos testes de UI;
 *  - falha de mídia DOWNLOADÍVEL (`failVideoDownload`), para provar que item ruim é
 *    pulado e o loop continua (critério 5 da §9).
 */
export class StubGateway implements ReceptorGateway {
  private readonly clockNow: () => Date;
  private readonly latencyMs: number;
  private readonly screenName: string;
  private readonly failVideoDownload: boolean;
  private readonly realtimeOnSubscribe: boolean;
  private ads: Ad[];
  private screenId: ID;
  /** Quantos `fetchQueue` já rodaram — a UI de demo mostra no log. */
  private fetchCount = 0;
  private heartbeatCount = 0;
  private readonly subscribers = new Set<(version: string) => void>();

  constructor(options: StubGatewayOptions = {}) {
    this.clockNow = options.now ?? defaultNow;
    this.latencyMs = options.latencyMs ?? 0;
    this.screenId = 'scr_demo';
    this.screenName = options.screenName ?? 'TV Demonstração';
    this.failVideoDownload = options.failVideoDownload ?? false;
    this.realtimeOnSubscribe = options.realtimeOnSubscribe ?? false;
    this.ads = options.ads ?? demoAds(this.screenId, this.clockNow());
  }

  now(): Date {
    return this.clockNow();
  }

  get name(): string {
    return this.screenName;
  }

  /** Nome da tela para a UI de espera; o `HttpGateway` expõe o mesmo pelo heartbeat. */
  screenLabel(): string {
    return this.screenName;
  }

  /** Números de chamada — a verificação de demo mostra que o loop de rede roda. */
  stats(): { fetchQueue: number; heartbeat: number; subscribers: number } {
    return { fetchQueue: this.fetchCount, heartbeat: this.heartbeatCount, subscribers: this.subscribers.size };
  }

  /** Adiciona/remove anúncios em runtime (a demo tem um botão "pausar 1º anúncio"). */
  setAds(ads: Ad[]): void {
    this.ads = ads;
    this.emit(this.queueVersion());
  }

  /** Remove um anúncio do loop — o atalho de "pausar" para inspeção humana. */
  pauseAd(adId: ID): boolean {
    const target = this.ads.find(ad => ad.id === adId);
    if (!target) {
      return false;
    }
    this.ads = this.ads.map(ad => (ad.id === adId ? { ...ad, status: 'paused' as const } : ad));
    this.emit(this.queueVersion());
    return true;
  }

  async fetchQueue(knownVersion?: string): Promise<QueuePayload | null> {
    await this.delay();
    this.fetchCount += 1;
    const now = this.clockNow();
    const payload = this.buildPayload(now);
    // 304: comparação EXATA do ETag, como o backend faz (`http/etag.ts`).
    if (knownVersion && knownVersion.trim() === payload.version) {
      return null;
    }
    return payload;
  }

  async fetchMediaBytes(media: MediaAsset): Promise<Blob> {
    await this.delay();
    if (this.failVideoDownload && media.id === 'med_demo_video') {
      throw new GatewayError('NETWORK', 'Falha simulada ao baixar o vídeo.');
    }
    if (media.kind === 'video') {
      return demoVideoBlob();
    }
    const label = MEDIA_LABELS[media.id] ?? {
      title: 'ANÚNCIO',
      subtitle: media.id,
      bg: '#1c2233',
      fg: '#e8ecff',
    };
    return svgPoster(label.title, label.subtitle, label.bg, label.fg);
  }

  async heartbeat(state: { queueVersion?: string; currentAdId?: string } = {}): Promise<HeartbeatResult> {
    await this.delay();
    this.heartbeatCount += 1;
    const now = this.clockNow();
    return {
      ack: true,
      serverTime: now.toISOString(),
      queueOutdated: state.queueVersion !== undefined && state.queueVersion !== this.queueVersion(),
      desiredQueueVersion: this.queueVersion(),
      heartbeatIntervalSeconds: 60,
      id: this.screenId,
      name: this.screenName,
      status: 'online',
    };
  }

  subscribeQueueUpdates(onChange: (version: string) => void): () => void {
    this.subscribers.add(onChange);
    if (this.realtimeOnSubscribe) {
      // SSE é caminho primário (D2): na demo ele deve disparar sozinho, senão a
      // inspeção humana nunca veria a fila mudar sem recarregar a página.
      queueMicrotask(() => {
        if (this.subscribers.has(onChange)) {
          onChange(this.queueVersion());
        }
      });
    }
    return () => {
      this.subscribers.delete(onChange);
    };
  }

  /** Dispara `queue.updated` para todos os assinantes (o `hub` do backend faz igual). */
  private emit(version: string): void {
    for (const subscriber of [...this.subscribers]) {
      subscriber(version);
    }
  }

  /** A `version` atual, calculada pelo DOMÍNIO — o mesmo número que o backend daria. */
  private queueVersion(): string {
    return this.buildPayload(this.clockNow()).version;
  }

  private buildPayload(now: Date): QueuePayload {
    // `buildPlaybackQueue` do domínio compartilhado: a fila da demo tem a MESMA
    // forma e a MESMA `version` que a fila real do backend, porque é a mesma função.
    const queue = buildPlaybackQueue(this.screenId, this.ads, now);
    const ads = this.ads.map(ad => ({
      ...ad,
      media: {
        ...ad.media,
        // URL ABSOLUTA: o contrato §4.1 promete absoluta e o receptor joga direto
        // no `<img src>`. No stub, `stub://` é intercepted pelo `ui/player.ts`.
        url: ad.media.url,
      },
    }));
    return {
      screenId: this.screenId,
      version: queue.version,
      generatedAt: queue.generatedAt,
      serverTime: now.toISOString(),
      rotationEpochSeconds: 1_767_225_600,
      items: queue.items,
      ads,
    };
  }

  private delay(): Promise<void> {
    if (this.latencyMs <= 0) {
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      setTimeout(resolve, this.latencyMs);
    });
  }
}

/** Blob de imagem (SVG) sem passar pelo gateway — atalho para testes. */
export function stubImageBlob(media: MediaAsset): Blob {
  const label = MEDIA_LABELS[media.id] ?? {
    title: 'ANÚNCIO',
    subtitle: media.id,
    bg: '#1c2233',
    fg: '#e8ecff',
  };
  return svgPoster(label.title, label.subtitle, label.bg, label.fg);
}
